/**
 * AuthorizationTab Component
 *
 * Tab content for displaying authorization status and managing authorization documents.
 * Based on SETTINGS_FUNCTIONAL_SPEC.md Section 1.6 "Authorization Tab"
 */

import {
  Check,
  AlertCircle,
  Download,
  ExternalLink,
} from 'lucide-react';
import type { CaseFile } from '../../types/settings';
import {
  getAuthStatusDisplay,
  formatAuthorityType,
} from '../../types/settings';

interface AuthorizationTabProps {
  caseFile: CaseFile;
}

export function AuthorizationTab({ caseFile }: AuthorizationTabProps): JSX.Element {
  const authStatus = getAuthStatusDisplay(caseFile.authorityType);

  // Calculate adult transition date (if applicable)
  const getTransitionDateDisplay = (): string | null => {
    if (caseFile.authorityType !== 'parent_of_minor') return null;

    // If adultTransitionDate is set, use it
    if (caseFile.adultTransitionDate) {
      const transitionDate = new Date(caseFile.adultTransitionDate);
      return transitionDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }) + ` (${caseFile.name} turns 18)`;
    }

    // Otherwise calculate from dateOfBirth
    if (caseFile.dateOfBirth) {
      const dob = new Date(caseFile.dateOfBirth);
      const transitionDate = new Date(dob);
      transitionDate.setFullYear(transitionDate.getFullYear() + 18);
      return transitionDate.toLocaleDateString('en-US', {
        month: 'long',
        day: 'numeric',
        year: 'numeric',
      }) + ` (${caseFile.name} turns 18)`;
    }

    return null;
  };

  // Get authorization documentation status
  const getDocumentationStatus = (): { type: string; status: string } | null => {
    const docs = caseFile.authorityDocumentation;
    if (!docs) return null;

    if (docs.parentalAttestationId) {
      return { type: 'Parental Attestation', status: 'On file' };
    }
    if (docs.guardianshipDocumentId) {
      return { type: 'Guardianship Document', status: 'Uploaded' };
    }
    if (docs.patientAuthorizationId) {
      return { type: 'Patient Authorization', status: 'On file' };
    }

    return null;
  };

  // Determine if documents are required
  const areDocumentsRequired = (): boolean => {
    return caseFile.authorityType === 'legal_guardian' || caseFile.authorityType === 'designated_agent';
  };

  const transitionDate = getTransitionDateDisplay();
  const docStatus = getDocumentationStatus();
  const docsRequired = areDocumentsRequired();

  return (
    <>
      {/* Authorization Status Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Authorization Status</h3>

          {/* Status Banner */}
          <div
            className={`flex items-center gap-3 p-4 rounded-lg mb-6 ${
              authStatus.color === 'green'
                ? 'bg-green-50'
                : 'bg-amber-50'
            }`}
          >
            <div
              className={`w-10 h-10 rounded-full flex items-center justify-center ${
                authStatus.color === 'green'
                  ? 'bg-evergreen text-white'
                  : 'bg-amber-500 text-white'
              }`}
            >
              {authStatus.color === 'green' ? (
                <Check className="w-5 h-5" />
              ) : (
                <AlertCircle className="w-5 h-5" />
              )}
            </div>
            <div>
              <h4 className="font-semibold text-ink">{authStatus.title}</h4>
              <p className="text-sm text-muted">
                {caseFile.authorityType
                  ? 'Authorization confirmed'
                  : 'Complete authorization to unlock all features'}
              </p>
            </div>
          </div>

          {/* Authority Type */}
          <div className="mb-6">
            <label className="label">Authority Type</label>
            <input
              type="text"
              value={formatAuthorityType(caseFile.authorityType)}
              disabled
              className="input bg-airy-gradient"
            />
          </div>

          {/* Adult Transition Date (only for parent_of_minor) */}
          {transitionDate && (
            <div className="mb-6">
              <label className="label">Adult Transition Date</label>
              <input
                type="text"
                value={transitionDate}
                disabled
                className="input bg-airy-gradient"
              />
              <p className="helper-text">
                We'll remind you to update authorization before this date.
              </p>
            </div>
          )}
        </div>
      </div>

      {/* Authorization Documents Card */}
      <div className="card mb-6">
        <div className="p-6">
          <h3 className="text-lg font-bold text-ink mb-5">Authorization Documents</h3>

          {!docsRequired ? (
            <p className="text-muted text-sm">
              {caseFile.authorityType === 'patient_self'
                ? 'No documents required for self-managed accounts.'
                : 'No additional documents required for parent of minor.'}
            </p>
          ) : docStatus ? (
            <div className="border border-soft-neutral rounded-lg p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="font-semibold text-ink">{docStatus.type}</p>
                  <p className="text-sm text-muted">{docStatus.status}</p>
                </div>
                <div className="flex items-center gap-2">
                  <button className="btn-ghost btn-sm flex items-center gap-1">
                    <ExternalLink className="w-4 h-4" />
                    View
                  </button>
                  <button className="btn-ghost btn-sm flex items-center gap-1">
                    <Download className="w-4 h-4" />
                    Download
                  </button>
                </div>
              </div>
            </div>
          ) : (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div className="flex items-start gap-2">
                <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="text-sm font-medium text-amber-800">
                    Documentation Required
                  </p>
                  <p className="text-xs text-amber-700 mt-1">
                    {caseFile.authorityType === 'legal_guardian'
                      ? 'Please upload guardianship documentation to complete authorization.'
                      : 'Patient authorization is pending. The care recipient will need to approve your access.'}
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Locked Status Warning (if applicable) */}
      {caseFile.isLocked && (
        <div className="alert alert-warning flex items-start gap-2">
          <AlertCircle className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold">CaseFile is Locked</p>
            <p className="text-sm mt-1">
              {caseFile.lockReason === 'pending_patient_authorization' &&
                'Waiting for patient to approve your access.'}
              {caseFile.lockReason === 'pending_adult_transition' &&
                'The care recipient has turned 18. Please complete the adult transition process.'}
              {caseFile.lockReason === 'authorization_revoked' &&
                'Authorization has been revoked. Contact the care recipient to regain access.'}
            </p>
          </div>
        </div>
      )}
    </>
  );
}

export default AuthorizationTab;
